/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import moment from 'moment';
import _ from 'lodash';

import { Link } from 'react-router-dom';

import { connect } from '../../components/BucketedDataTable';
import Localized from '../../../shared/components/Localized';

import { DATE_FORMAT, TIME_FORMAT } from "../../../shared/utils/dates";
import { hyperforceDateWindow } from "../../../config";

const HYPERFORCE_WINDOW_RELEASE_DAYS = hyperforceDateWindow;

const columns = [
  {
    title: 'ID',
    key: 'id',
    sizeLarge: 1,
    sizeMedium: 1,
    sizeSmall: 2,
    render: (row) => {
      return (
        <Link
          key={row.id}
          to={{
            pathname: `/maintenances/${row.id}`,
            state: { modal: true }
          }}
        >
          <div>{row.id}</div>
        </Link>
      );
    }
  },
  {
    title: <Localized modelKey='date' modelAttribute='header' modelName='bucketedList' />,
    key: 'date',
    render: (row, args) => row.plannedStartTime.tz(args.timezone).format(DATE_FORMAT),
    sizeLarge: 1,
    sizeMedium: 1.5,
    sizeSmall: 2
  },
  {
    title: <Localized modelKey='status' modelAttribute='header' modelName='bucketedList' />,
    key: 'status',
    render: (row) => {
      const status = row.message.eventStatus;
      const className = (status === 'eventStatusCanceled' || status === 'maintenanceCancellation') ? 'maintenance-status-canceled' : undefined;

      return <Localized modelName={'fieldOption'} modelKey={status} modelAttribute={'label'} className={className} />;
    },
    sizeLarge: 1,
    sizeMedium: 1,
    sizeSmall: 1,
  },
  {
    title: <Localized modelKey='startTime' modelAttribute='header' modelName='bucketedList' />,
    key: 'startTime',
    render: (row, args) => {
      const now = moment().utc();
     const isRelease =row.message?.maintenanceType?.toLowerCase().includes('release');
     const isHyperforceCore = isRelease && row.substrate?.toLowerCase().includes('hyperforce core');
     const daysUntilStart = row.plannedStartTime.diff(now, 'days');
     const showAsStartDate = isHyperforceCore && daysUntilStart > HYPERFORCE_WINDOW_RELEASE_DAYS;
     if (!showAsStartDate) {
       return row.plannedStartTime.tz(args.timezone).format(TIME_FORMAT);
     }
   },
    sizeLarge: 1,
    sizeMedium: 1.5,
    hideSmall: true
  },
  {
    title: <Localized modelKey='subject' modelAttribute='header' modelName='bucketedList' />,
    key: 'name',
    sizeLarge: 3,
    sizeMedium: 4,
    sizeSmall: 8
  },
  {
    title: <Localized modelKey='instances' modelAttribute='header' modelName='bucketedList' />,
    key: 'instances',
    render: (row) => (
      row.instanceKeys.map((instanceKey, index) => {
        if (index === row.instanceKeys.length-1) {
          return <Localized modelName={'instance'} modelKey={instanceKey} modelAttribute={'label'} key={index} />;
        }

        return <span key={index} ><Localized modelName={'instance'} modelKey={instanceKey} modelAttribute={'label'} />, </span>;
      })
    ),
    sizeLarge: 2,
    sizeMedium: 4,
    hideSmall: true
  },
  {
    title: <Localized modelKey='services' modelAttribute='header' modelName='bucketedList' />,
    key: 'services',
    render: (row) => (
      row.serviceKeys.map((serviceKey, index) => {
        if (index === row.serviceKeys.length-1) {
          return <Localized modelName={'service'} modelKey={serviceKey} modelAttribute={'label'} key={index} />;
        }

        return <span key={index} ><Localized modelName={'service'} modelKey={serviceKey} modelAttribute={'label'} />, </span>;
      })
    ),
    sizeLarge: 2,
    hideMedium: true,
    hideSmall: true,
  },
  {
    title: <Localized modelKey='type' modelAttribute='header' modelName='bucketedList' />,
    key: 'maintenanceType',
    render: (row) => <Localized modelName={'fieldOption'} modelKey={row.message.maintenanceType} modelAttribute={'label'} />,
    sizeLarge: 2,
    hideMedium: true,
    hideSmall: true,
  }
];

const maintenanceBuckets = [];

const cutoff = moment().subtract(33, 'day').startOf('day');
const today = moment().startOf('day');
const endOfMonth = moment().endOf('month');

maintenanceBuckets.push({
  title: (rows) => `Past 33 Days (${rows.length})`,
  partition: (rows) => rows.filter((maintenance) => {
    return maintenance.plannedStartTime &&
      maintenance.plannedStartTime.isBefore(today) &&
      maintenance.plannedStartTime.isAfter(cutoff);
  }),
  open: false
});

maintenanceBuckets.push({
  title: (rows) => `Today - ${endOfMonth.format('MMMM')} (${rows.length})`,
  partition: (rows) => rows.filter((maintenance) => {
    return maintenance.plannedStartTime &&
      maintenance.plannedStartTime.isBefore(endOfMonth) &&
      maintenance.plannedStartTime.isAfter(today);
  }),
  open: false
});

_.range(1, 12).map((i) => {
  const month = moment().add(i, 'month').startOf('month');

  // If this year only show month, if next year show month and year
  const formattedString = month.isSame(moment(), 'year') ? 'MMMM' : 'MMMM YYYY';

  maintenanceBuckets.push({
    title: (rows) => `${month.format(formattedString)} (${rows.length})`,
    partition: (rows) => rows.filter((maintenance) => {
      return maintenance.plannedStartTime && maintenance.plannedStartTime.isSame(month, 'month');

    }),
    open: false
  });
});

const filterByString = (value, filterState) => {
  const searchString = value ? value.toUpperCase() : '';

  return searchString.includes(filterState.toUpperCase());
};

const filterByDate = (date, filterState) => {
  return date.format(DATE_FORMAT).includes(filterState)
    || date.format(TIME_FORMAT).includes(filterState);
};

const filterByInstanceKey = (instances, filterState) => {
  let shouldInclude = false;
  _.forEach(instances, (instance) => {
    shouldInclude = instance.toUpperCase().includes(filterState.toUpperCase());
    if (shouldInclude) return false;
  });
  return shouldInclude;
};

const filterColumns = [{
  key: 'name',
  test: filterByString
}, {
  key: 'plannedStartTime',
  test: filterByDate
}, {
  key: 'instanceKeys',
  test: filterByInstanceKey
}];

function filter(row, filterState) {

  // Filter out maintenances with a null start time to avoid crashing the sort function
  // Note: This needs to be before any other filters in case those return true
  if (row.plannedStartTime === null) {
    return false;
  }

  if (filterState === '') return true; // If no filter set, all maintenances match

  let shouldInclude = false;

  _.forEach(filterColumns, (column) => {
    if (row[column.key]) {
      shouldInclude = column.test(row[column.key], filterState);
      if (shouldInclude) return false;
    }
  });
  return shouldInclude;
}

function sort(a, b) {
  return a.plannedStartTime.diff(b.plannedStartTime)
}

export default connect(columns, maintenanceBuckets, filter, sort);

